import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
const ConsultForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="37046" className="p-8 bg-primary/10 border border-primary/20 rounded-xl text-center animate-in fade-in zoom-in duration-300">
        <h3 className="text-xl font-bold text-primary mb-2">Transmission Successful</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-[0_0_15px_rgba(0,255,255,0.3)]">
          Submit New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Contact Person is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-mono uppercase tracking-wider text-xs">Contact Person</FormLabel>
              <FormControl>
                <Input placeholder="Alex Chen" {...field} className="border-border bg-background/50 rounded-lg focus-visible:ring-primary text-foreground" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company_name" rules={{
        required: "Company Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-mono uppercase tracking-wider text-xs">Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Nexel Partner Corp" {...field} className="border-border bg-background/50 rounded-lg focus-visible:ring-primary text-foreground" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "Corporate Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-mono uppercase tracking-wider text-xs">Corporate Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="engineering@nexel.tech" {...field} className="border-border bg-background/50 rounded-lg focus-visible:ring-primary text-foreground" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-mono uppercase tracking-wider text-xs">Contact Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 010-9988" {...field} className="border-border bg-background/50 rounded-lg focus-visible:ring-primary text-foreground" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select an area of interest"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-mono uppercase tracking-wider text-xs">Interest Area</FormLabel>
            <FormControl>
              <select {...field} className="flex h-9 w-full items-center justify-between rounded-lg border border-border bg-background/50 px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                <option value="" disabled>Select Infrastructure Type</option>
                <option value="Optical Fiber">Optical Fiber Solutions (G652D/G657)</option>
                <option value="Drone Systems">FPV Drone Systems & Components</option>
                <option value="AI Data Centers">AI Data Center Connectivity</option>
                <option value="Both">Integrated Hybrid Solutions</option>
                <option value="Other">Other Technical Inquiry</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-mono uppercase tracking-wider text-xs">Technical Requirements</FormLabel>
            <FormControl>
              <Textarea placeholder="Specify fiber attenuation specs, drone flight controller preferences, or data center bandwidth needs..." {...field} className="min-h-[120px] border-border bg-background/50 rounded-lg focus-visible:ring-primary text-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-mono">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-6 text-lg rounded-lg transition-all shadow-[0_0_20px_rgba(0,255,255,0.3)] hover:shadow-[0_0_30px_rgba(0,255,255,0.5)] border-none">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Synchronizing...
          </> : "Initiate Partnership"}
      </Button>
    </Form>;
};
export default function ConsultWithOurExperts() {
  return <section id="initiate-partnership" className="py-24 bg-background dark">
      <div className="container mx-auto px-4">
        <div className="max-w-[768px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground font-sans mb-4 tracking-tight">
              Scale Your Infrastructure
            </h2>
            <p className="text-lg text-muted-foreground font-sans max-w-2xl mx-auto">
              Connect with our engineering specialists to integrate next-generation optical fiber and FPV drone systems into your global operations.
            </p>
          </div>

          <div className="bg-card/40 backdrop-blur-md border border-border rounded-xl shadow-2xl p-1">
            <FormProvider formKey="partnership_form_home" formId="partnership_form_home" sectionName="partnership-section" defaultValues={{
            full_name: "",
            company_name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            company_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            inquiry_type: {
              required: true
            }
          }} submitText="Initiate Partnership" successMessage="Protocol accepted. Our technical team will review your requirements and respond within 24 hours.">
              <ConsultForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}